'use client';

import React, { useCallback, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { Link } from '@/i18n/routing';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';

import type { IMenuItem } from '@/interfaces/collection.interface';

import { ELanguage } from '@/enums/language.enum';

import 'swiper/css';
import 'swiper/css/navigation';

type TNavLinksProps = {
  menus: IMenuItem[];
  setMobileMenuOpen?: (isOpen: boolean) => void;
};

const DesktopNavLinks: React.FC<TNavLinksProps> = ({ setMobileMenuOpen, menus }) => {
  const pathname = usePathname();
  const locale: ELanguage = useLocale() as ELanguage;

  const [hoveredMenu, setHoveredMenu] = useState<IMenuItem | null>(null);
  const [showNavButtons, setShowNavButtons] = useState(false);

  const navRef = useRef<HTMLDivElement>(null);
  const swiperRef = useRef<any>(null);

  const handleSwiperInit = (swiper: any) => {
    swiperRef.current = swiper;
    checkScroll(swiper);
  };

  const checkScroll = (swiper: any) => {
    const { slides, width, slidesGrid } = swiper;
    const firstSlide = slides[0];
    if (!firstSlide) return;

    const lastSlideOffset = slidesGrid[slidesGrid.length - 1];
    setShowNavButtons(lastSlideOffset + firstSlide.offsetWidth > width);
  };

  const handleResize = () => {
    if (swiperRef.current) checkScroll(swiperRef.current);
  };

  const handleClick = useCallback(() => setMobileMenuOpen?.(false), [setMobileMenuOpen]);

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="relative w-full" ref={navRef}>
      <div className="custom-swiper-nav flex w-full items-center justify-center gap-2">
        {showNavButtons && (
          <button
            onClick={() => swiperRef.current?.slidePrev()}
            className="text-black transition hover:text-clr-20 dark:text-white dark:hover:text-orange-500"
          >
            <FaChevronLeft />
          </button>
        )}
        <Swiper
          onInit={handleSwiperInit}
          onResize={handleResize}
          modules={[Navigation]}
          spaceBetween={35}
          slidesPerView="auto"
          className="custom-swiper-nav w-fit max-w-full"
        >
          {menus?.map((menu: IMenuItem, index: number) => (
            <SwiperSlide key={`menu_${index}`} style={{ width: 'auto' }}>
              <div
                onMouseEnter={() => setHoveredMenu(menu)}
                onMouseLeave={() => setHoveredMenu(null)}
                className="xl:py-[42px]"
              >
                <Link
                  href={menu.isMenu ? `/shop?collection=${menu.slug}` : `/shop?menu=${menu.slug}`}
                  className={`${
                    pathname.includes(menu.slug)
                      ? 'text-clr-20 dark:text-white'
                      : 'text-black dark:text-white dark:hover:text-clr-20'
                  } whitespace-nowrap text-base hover:text-clr-20`}
                  onClick={handleClick}
                >
                  {menu.title?.[locale] || menu.title?.[ELanguage.ENGLISH]}
                </Link>
              </div>
            </SwiperSlide>
          ))}

          <SwiperSlide style={{ width: 'auto' }}>
            <div className="xl:py-[42px]">
              <Link
                href="/stories"
                className={`${
                  pathname === `/${locale}/stories` ? 'text-clr-20 dark:text-white' : 'text-black dark:text-white'
                } whitespace-nowrap text-base hover:text-clr-20 dark:hover:text-clr-20`}
                onClick={handleClick}
              >
                Stories
              </Link>
            </div>
          </SwiperSlide>
          <SwiperSlide style={{ width: 'auto' }}>
            <div className="xl:py-[42px]">
              <Link
                href={`/explore-the-brand`}
                className={`${
                  pathname === `/${locale}/explore-the-brand`
                    ? 'text-clr-20 dark:text-white'
                    : 'text-black dark:text-white'
                } whitespace-nowrap text-base hover:text-clr-20 dark:hover:text-clr-20`}
                onClick={handleClick}
              >
                Explore
              </Link>
            </div>
          </SwiperSlide>
        </Swiper>

        {showNavButtons && (
          <button
            onClick={() => swiperRef.current?.slideNext()}
            className="text-black transition hover:text-clr-20 dark:text-white dark:hover:text-clr-20"
          >
            <FaChevronRight />
          </button>
        )}
      </div>

      {(hoveredMenu?.categories?.length ?? 0) > 0 && (
        <div
          className="absolute left-0 top-full z-50 flex w-full justify-center gap-20 p-4 shadow-lg transition-all duration-300 ease-in-out"
          onMouseEnter={() => setHoveredMenu(hoveredMenu)}
          onMouseLeave={() => setHoveredMenu(null)}
        >
          {hoveredMenu?.categories?.map((category, catIndex) => (
            <div key={`category_${catIndex}`} className="mb-4">
              <Link href={`/shop?category=${category.slug}`}>
                <h3 className="mb-2 text-sm dark:text-white hover:text-clr-20 dark:hover:text-clr-20">
                  {category.title[locale] || category.title?.[ELanguage.ENGLISH]}
                </h3>
              </Link>
              <div className="flex flex-col gap-2">
                {category?.collections?.map((subcategory, subIndex) => (
                  <Link
                    key={`subCategory_${subIndex}`}
                    href={`/shop?category=${category.slug}&collection=${subcategory.slug}`}
                    className="block text-sm font-light text-black hover:text-clr-20 dark:text-white dark:hover:text-clr-20"
                    onClick={handleClick}
                  >
                    {subcategory.title[locale] || subcategory.title?.[ELanguage.ENGLISH]}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DesktopNavLinks;
